/**
 * What a valid ingestion rule is, and how its form values map to and from the
 * wire.
 *
 * The invocation is the trigger form's own invocation block, validated by the
 * same schema, so a rule and a trigger target hold an action the same way. It
 * is validated only while auto-ingest is on: a rule without auto-ingest runs
 * nothing, so a half-filled invocation is kept as a draft rather than refused.
 */

import { z } from "zod";

import { actionInvocationSpecOf } from "@/shared/components/invocationSpec/buildInvocationSpec";
import { invocationSpecValuesFromWire } from "@/shared/components/invocationSpec/invocationSpecFromWire";
import {
  defaultInvocationSpecValues,
  invocationSpecSchema,
  missingRequiredParameters,
  type InvocationSpecValues,
} from "@/shared/components/invocationSpec/invocationSpecSchema";
import {
  type CreateIngestionRuleRequest,
  type IngestionRulePrefill,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";

export const ingestionRuleFormSchema = z
  .object({
    name: z.string().trim().min(1, "Give the rule a name."),
    description: z.string(),
    includePatterns: z
      .array(z.string())
      .min(1, "Add at least one path pattern, such as **/*.mcap."),
    excludePatterns: z.array(z.string()),
    invocation: z.custom<InvocationSpecValues>(),
    /**
     * The chosen action's required parameters, noted by the editor once the
     * action loads. Not sent.
     */
    requiredParameters: z.array(z.string()),
    autoIngest: z.boolean(),
  })
  .superRefine(({ autoIngest, invocation, requiredParameters }, ctx) => {
    if (!autoIngest) {
      return;
    }
    const parsed = invocationSpecSchema.safeParse(invocation);
    for (const issue of parsed.error?.issues ?? []) {
      ctx.addIssue({
        code: "custom",
        path: ["invocation", ...issue.path],
        message: issue.message,
      });
    }
    for (const name of missingRequiredParameters(
      invocation.parameters,
      requiredParameters,
    )) {
      ctx.addIssue({
        code: "custom",
        path: ["invocation", "parameters", name],
        message: "The action needs this. Enter a value.",
      });
    }
  });

export type IngestionRuleFormValues = z.input<typeof ingestionRuleFormSchema>;
export type IngestionRuleFormOutput = z.output<typeof ingestionRuleFormSchema>;

export function emptyIngestionRuleFormValues(): IngestionRuleFormValues {
  return {
    name: "",
    description: "",
    includePatterns: [],
    excludePatterns: [],
    invocation: defaultInvocationSpecValues(),
    requiredParameters: [],
    autoIngest: true,
  };
}

export function ingestionRuleToFormValues(
  rule: IngestionRuleRecord,
): IngestionRuleFormValues {
  return {
    name: rule.name,
    description: rule.description ?? "",
    includePatterns: [...rule.include_patterns],
    excludePatterns: [...rule.exclude_patterns],
    invocation:
      rule.invocation === null
        ? defaultInvocationSpecValues()
        : invocationSpecValuesFromWire(rule.invocation),
    requiredParameters: [],
    autoIngest: rule.auto_ingest,
  };
}

/** A new rule's values, seeded from what a file or a trigger sent over. */
export function ingestionRulePrefillToFormValues(
  prefill: IngestionRulePrefill,
): IngestionRuleFormValues {
  const empty = emptyIngestionRuleFormValues();
  return {
    ...empty,
    name: prefill.name ?? "",
    includePatterns: [...prefill.includePatterns],
    excludePatterns: [...prefill.excludePatterns],
    invocation:
      prefill.invocation === undefined
        ? empty.invocation
        : invocationSpecValuesFromWire(prefill.invocation),
  };
}

/**
 * The create request the form's values add up to; also the full update body.
 * The uploaded file is not among the inputs: the managed trigger always passes
 * it first, so only the inputs added beside it are sent. With auto-ingest off,
 * an invocation that is not complete is left out, so an update keeps the one
 * the rule has.
 */
export function buildIngestionRuleRequest(
  values: IngestionRuleFormOutput,
): CreateIngestionRuleRequest {
  const description = values.description.trim();
  const invocation = invocationSpecSchema.safeParse(values.invocation);
  return {
    name: values.name.trim(),
    description: description === "" ? null : description,
    include_patterns: values.includePatterns,
    exclude_patterns: values.excludePatterns,
    ...(invocation.success
      ? { invocation: actionInvocationSpecOf(invocation.data) }
      : {}),
    auto_ingest: values.autoIngest,
  };
}
